import { useEffect, useState } from 'react';
import { useApolloClient, type ApolloClient } from '@apollo/client';
import {
    GetAutoApplyScreenerPrefillStateDocument,
    type GetAutoApplyScreenerPrefillStateQuery,
    type GetAutoApplyScreenerPrefillStateQueryVariables,
} from './__generated__/ApplicationTabContent.generated';
import { hasPrefilledScreenerAnswer } from '../questions/screenerQuestionUtils';
import type { ScreenerQuestionContextQuestion } from '../../../__generated__/graphql-base-types';
import { ROOT_TRACKING_KEY_HEADER } from '../../../lib/apollo/headers';
import type { AutoApplyDiagnostics } from '../autoApplyDiagnostics';

/**
 * Checks actual screener prefill for every draft, including drafts without a questions step:
 * skipping that step can mean either fully prefilled questions or no questions at all.
 * Shared by Review and Confirmation; retains the disclaimer if prefill cannot be determined.
 *
 * `ScreenerQuestionContextQuestion` is pinned to `keyFields: false` in the cache (client.ts), so the
 * non-unique screener ids can't collapse questions across (or within) drafts on the cached read.
 */
export const resolveShowAiAutofillDisclaimer = async (
    client: ApolloClient<object>,
    draftId: string | undefined,
    iaUid: string | undefined,
    diagnostics?: AutoApplyDiagnostics,
    ordinal?: number,
): Promise<boolean> => {
    const finish = diagnostics?.start('prefill', ordinal);
    if (!draftId || !iaUid) {
        finish?.('fallback');
        return true;
    }
    try {
        const { data, error } = await client.query<
            GetAutoApplyScreenerPrefillStateQuery,
            GetAutoApplyScreenerPrefillStateQueryVariables
        >({
            query: GetAutoApplyScreenerPrefillStateDocument,
            variables: { input: { draftId } },
            fetchPolicy: 'cache-first',
            context: { headers: { [ROOT_TRACKING_KEY_HEADER]: iaUid } },
        });
        if (error) {
            finish?.('fallback');
            return true; // fail open: keep the disclaimer
        }
        const questions = (data.smartApplyContext?.screenerQuestionContext?.screenerQuestions ??
            []) as ScreenerQuestionContextQuestion[];
        const value = hasPrefilledScreenerAnswer(questions);
        finish?.('success');
        return value;
    } catch {
        finish?.('fallback');
        return true; // fail open (network error)
    }
};

/**
 * Whether the review preview should embed the server's AI-autofill disclaimer.
 *
 * - `true` — at least one screener question was prefilled, or prefill cannot be determined.
 * - `false` — no prefilled answers, including drafts with no screener questions.
 * - `undefined` — still resolving; callers must defer preview generation until it settles.
 */
export const useShowAiAutofillDisclaimer = (
    draftId: string | undefined,
    iaUid: string | undefined,
    diagnostics?: AutoApplyDiagnostics,
): boolean | undefined => {
    const client = useApolloClient();
    const [decided, setDecided] = useState<{
        draftId: string;
        iaUid: string;
        value: boolean;
    }>();

    useEffect(() => {
        if (!draftId || !iaUid) return;
        let active = true;
        void resolveShowAiAutofillDisclaimer(client, draftId, iaUid, diagnostics).then((value) => {
            if (active) setDecided({ draftId, iaUid, value });
        });
        return () => {
            active = false;
        };
    }, [client, draftId, iaUid, diagnostics]);

    if (!draftId || !iaUid) return true;
    return decided?.draftId === draftId && decided.iaUid === iaUid ? decided.value : undefined;
};
